import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DocsList from "./DocsList";
import DeviceGuidesList from "./DeviceGuidesList";
import DocViewer from "./DocViewer";
import Header from "../../Templates/Header";
import { devicesRegistry } from "./data/deviceRegistrationSteps";
import { CONTENT_SEARCH_INDEX } from "../../../generated/searchContentIndex";

const findDeviceIdForDoc = (docId) =>
  devicesRegistry.find((device) => device.guides?.some((guide) => guide.id === docId))?.id || null;

const IndexedReferenceResult = ({ lookup, onBack }) => {
  const entry = CONTENT_SEARCH_INDEX.find(
    (item) => (lookup.api && item.kind === "api" && item.title === lookup.api) || (lookup.ref && item.anchor === lookup.ref)
  );

  return (
    <Box sx={{ height: "100%", overflow: "auto", backgroundColor: "background.default", color: "text.primary" }}>
      <Header title="Documentation" subtitle="Step-by-step guides, API references, and tutorials for the Vecros platform." />
      <Box id={entry?.anchor || lookup.ref} sx={{ p: { xs: 2, md: 4 }, maxWidth: 880 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          size="small"
          sx={{ mb: 3, textTransform: "none" }}
        >
          Back to docs
        </Button>
        <Typography variant="h4" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, mb: 1 }}>
          {entry?.title || lookup.api || lookup.ref}
        </Typography>
        {entry ? (
          <>
            <Typography sx={{ color: "text.secondary", mb: 3 }}>{entry.subtitle}</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8, fontSize: 14 }}>
              {entry.text}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: "text.secondary" }}>
            This entry is not in the generated search index. Rebuild the search index after updating docs/openapi.json.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const DocsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const urlDocId = params.get("doc");
  const apiId = params.get("api");
  const refId = params.get("ref");
  const initialDeviceId = urlDocId ? findDeviceIdForDoc(urlDocId) : null;
  const [view, setView] = useState(urlDocId ? "viewer" : "devices"); // "devices" | "device-guides" | "viewer"
  const [selectedDeviceId, setSelectedDeviceId] = useState(initialDeviceId);
  const [selectedDocId, setSelectedDocId] = useState(urlDocId);

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    const nextDocId = nextParams.get("doc");
    if (!nextDocId) return;

    setSelectedDocId(nextDocId);
    setSelectedDeviceId(findDeviceIdForDoc(nextDocId));
    setView("viewer");
  }, [location.search]);

  const handleSelectDevice = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setView("device-guides");
    navigate("/services/developer/docspage", { replace: true });
  };

  const handleSelectDoc = (docId) => {
    setSelectedDocId(docId);
    setView("viewer");
    navigate(`/services/developer/docspage?doc=${encodeURIComponent(docId)}`, { replace: true });
  };

  const handleBackToDevices = () => {
    setSelectedDeviceId(null);
    setView("devices");
    navigate("/services/developer/docspage", { replace: true });
  };

  const handleBackToGuides = () => {
    setSelectedDocId(null);
    setView("device-guides");
    navigate("/services/developer/docspage", { replace: true });
  };

  return (
    <Box sx={{ height: "100%", overflow: "hidden" }}>
      {(apiId || refId) && (
        <IndexedReferenceResult
          lookup={{ api: apiId, ref: refId }}
          onBack={() => navigate("/services/developer/docspage", { replace: true })}
        />
      )}
      {!apiId && !refId && view === "devices" && (
        <DocsList onSelectDevice={handleSelectDevice} />
      )}
      {!apiId && !refId && view === "device-guides" && selectedDeviceId && (
        <DeviceGuidesList
          deviceId={selectedDeviceId}
          onSelectDoc={handleSelectDoc}
          onBack={handleBackToDevices}
        />
      )}
      {!apiId && !refId && view === "viewer" && selectedDocId && (
        <DocViewer
          docId={selectedDocId}
          onBack={handleBackToGuides}
        />
      )}
    </Box>
  );
};

export default DocsPage;
